import forge from 'apis/forge';
import { getBoundingBox } from 'dragonmouse-shared/geometry';
import { useTransformAnimation } from 'hooks/useTransformAnimation';
import { useEffect, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
	DEFAULT_CANVAS_COLOR,
	selectActiveScene,
	selectFrames,
	selectScenes,
	selectUser,
} from 'redux/db/reducers';
import { getFrameBounds } from 'redux/db/util';
import {
	cancelTransformAnimation,
	centerCanvasAroundSceneBoundingBox,
	setCanvasHeight,
	setCanvasWidth,
} from 'redux/mode/actions';
import { selectTransform, selectTransformInitialized } from 'redux/mode/reducers';
import { RootReducer, selectIsLoadingAnything } from 'redux/reducers';
import Smartlook from 'smartlook-client';
import Connections from '../Connections';
import FrameView from '../FrameView';
import * as S from './styles';

const Canvas = () => {
	const dispatch = useDispatch();

	const {
		user,
		frames,
		scene,
		activeSceneId,
		transform,
		transformsInitialized,
		transformAnimation,
		connectingLinesEnabled,
		loading,
	} = useSelector(
		(r: RootReducer) => ({
			user: selectUser(r.db),
			frames: selectFrames(r.db),
			scenes: selectScenes(r.db),
			scene: selectActiveScene(r.db, r.mode.activeSceneId),
			activeSceneId: r.mode.activeSceneId,
			transform: selectTransform(r.mode),
			transformsInitialized: selectTransformInitialized(r.mode),
			transformAnimation: r.mode.transformAnimation,
			connectingLinesEnabled: r.mode.settings.connectingLinesEnabled,
			loading: selectIsLoadingAnything(r),
		}),
		shallowEqual
	);

	useEffect(() => {
		if (!user || !activeSceneId) {
			return;
		}

		if (user.metadata?.lastSceneId !== activeSceneId) {
			forge.updateUser({
				update: {
					id: user.id,
					metadata: {
						lastSceneId: activeSceneId,
					},
				},
			});
		}
	}, [user?.id, activeSceneId]);

	const canvasRef = useRef() as React.MutableRefObject<HTMLDivElement>;

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------
	useTransformAnimation(transformAnimation, activeSceneId);

	// on dismount, cancel any transform animation
	useEffect(() => {
		return () => {
			dispatch(cancelTransformAnimation());
		};
	}, [dispatch]);

	useEffect(() => {
		if (canvasRef.current) {
			canvasRef.current.clientWidth && dispatch(setCanvasWidth(canvasRef.current.clientWidth));
			canvasRef.current.clientHeight && dispatch(setCanvasHeight(canvasRef.current.clientHeight));
		}

		// Use ResizeObserver to watch for canvas size changes
		const resizeObserver = new ResizeObserver(() => {
			if (canvasRef.current) {
				canvasRef.current.clientWidth && dispatch(setCanvasWidth(canvasRef.current.clientWidth));
				canvasRef.current.clientHeight && dispatch(setCanvasHeight(canvasRef.current.clientHeight));
			}
		});

		if (canvasRef.current) {
			resizeObserver.observe(canvasRef.current);
		}

		return function cleanup() {
			resizeObserver.disconnect();
		};
	}, [dispatch]);

	// initialize Smartlook, if needed
	useEffect(() => {
		if (scene && Smartlook.initialized()) {
			Smartlook.track('load_scene', {
				scene_id: scene.id,
				scene_title: scene.title,
			});
		}
	}, [activeSceneId, Smartlook.initialized()]);

	// initialize transforms, if needed
	useEffect(() => {
		if (!transformsInitialized && frames.length && !loading) {
			const bounds = getBoundingBox(frames.map((frame) => getFrameBounds(frame)));
			dispatch(
				centerCanvasAroundSceneBoundingBox({
					boundingBox: bounds,
					animated: false,
				})
			);
		}
	}, [activeSceneId, transformsInitialized, frames, loading]);

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	const canvasColor = scene?.metadata?.canvasColor ?? DEFAULT_CANVAS_COLOR;

	return (
		<S.Base color={canvasColor}>
			<S.Scene id="driver-canvas" ref={canvasRef} transform={transform}>
				{connectingLinesEnabled && <Connections />}
				{frames.map((frame: any) => (
					<FrameView key={frame.id} frame={frame} />
				))}
			</S.Scene>
		</S.Base>
	);
};

export default Canvas;
